import { useCallback, useEffect, useRef, useState } from 'react';
import { getAuthInit } from '../api/auth';
import { getErrorMessage, ACCOUNT_BLOCKED_EVENT } from '../api/client';
import { AccountBlockedPage } from '../pages/AccountBlockedPage';
import { useAuth } from '../auth/AuthProvider';
import {
  canViewBilling,
  canCountAssigned,
  canCreateShiftClose,
  canManagePurchases,
  canManageUsers,
  canRecordWaste,
  canViewExpenses,
  canViewOtherSales,
  canViewOwnerDashboard,
  canViewVoidRequests,
} from '../auth/permissions';
import { LoadingScreen } from '../components/LoadingScreen';
import { AppLayout, type AppSection } from '../layouts/AppLayout';
import { canCreateProduct, canOpenPos, destinationOf, legacyHashRedirect, sectionFromHash, shellSectionForHash } from '../layouts/navConfig';
import { AccessDeniedPage } from '../pages/AccessDeniedPage';
import { DashboardHomePage } from '../pages/DashboardHomePage';
import { LoginPage } from '../pages/LoginPage';
import { ExpensesPage } from '../pages/ExpensesPage';
import { OtherSalesPage } from '../pages/OtherSalesPage';
import { InventoryPage } from '../pages/InventoryPage';
import { LoyverseIntegrationPage } from '../pages/LoyverseIntegrationPage';
import { OwnerDashboardPage } from '../pages/OwnerDashboardPage';
import { SalesReceiptsPage } from '../pages/SalesReceiptsPage';
import { SetupPendingPage } from '../pages/SetupPendingPage';
import { ShiftClosePage } from '../pages/ShiftClosePage';
import { StoresPage } from '../pages/StoresPage';
import { UsersPage } from '../pages/UsersPage';
import { VoidRequestsPage } from '../pages/VoidRequestsPage';
import { FormsPage } from '../pages/FormsPage';
import { OperationalFormsPage } from '../pages/OperationalFormsPage';
import { CatalogApprovalsPage } from '../pages/CatalogApprovalsPage';
import { WastePage } from '../pages/WastePage';
import { CountSheetsPage } from '../pages/CountSheetsPage';
import { NuevoProductoPage } from '../pages/NuevoProductoPage';
import { BillingPage } from '../pages/BillingPage';
import { getOnboarding } from '../selfservice/api';
import type { AuthInitResult } from '../types/auth';

export function AppRouter() {
  const { isAuthenticated, isLoading: isAuthLoading, user, stores } = useAuth();
  const [authInit, setAuthInit] = useState<AuthInitResult | null>(null);
  const [initError, setInitError] = useState<string | null>(null);
  const [isInitLoading, setIsInitLoading] = useState(true);
  const [section, setSection] = useState<AppSection>(getSectionFromHash);
  // P-075 (D-1): la URL completa, para saber qué se resalta en el menú (`#other-sales/vender` = «Vender»).
  const [hash, setHash] = useState(() => window.location.hash);
  const [accountBlocked, setAccountBlocked] = useState<null | 'SUSPENDED' | 'ARCHIVED'>(null);
  const requestedRef = useRef(false);
  const onboardingCheckedRef = useRef(false);
  // Bloque E: ¿se ha navegado dentro de la app desde que cargó? Una pantalla que se abrió navegando
  // tiene detrás una entrada de la app a la que volver; una que se cargó directa (enlace, recarga), no.
  const navigatedInAppRef = useRef(false);

  const loadAuthInit = useCallback(async () => {
    setInitError(null);
    setIsInitLoading(true);
    try {
      setAuthInit(await getAuthInit());
    } catch (error) {
      setInitError(getErrorMessage(error, 'No fue posible iniciar la aplicación.'));
    } finally {
      setIsInitLoading(false);
    }
  }, []);

  useEffect(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;
    void loadAuthInit();
  }, [loadAuthInit]);

  useEffect(() => {
    const handleHashChange = () => { navigatedInAppRef.current = true; setSection(getSectionFromHash()); setHash(window.location.hash); };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Fase 25A: cuenta suspendida/archivada => pantalla neutral global (evita 403 crudos por la app).
  useEffect(() => {
    const onBlocked = (e: Event) => {
      const code = (e as CustomEvent<string>).detail;
      setAccountBlocked(code === 'BUSINESS_ARCHIVED' ? 'ARCHIVED' : 'SUSPENDED');
    };
    window.addEventListener(ACCOUNT_BLOCKED_EVENT, onBlocked);
    return () => window.removeEventListener(ACCOUNT_BLOCKED_EVENT, onBlocked);
  }, []);

  // Fase 27B: reanudación del onboarding. Un OWNER cuyo negocio aún no tiene tienda (señal precisa de
  // configuración pendiente; los negocios existentes/completados siempre tienen ≥1 tienda) se reenvía a
  // #onboarding SOLO si el backend confirma IN_PROGRESS. Se cede ante cuenta bloqueada (25A gana). Es una
  // sola llamada, gateada por stores.length===0, así que los tenants existentes nunca la disparan.
  useEffect(() => {
    if (onboardingCheckedRef.current) return;
    if (!isAuthenticated || !user || accountBlocked) return;
    if (user.role !== 'OWNER' || stores.length > 0) return;
    onboardingCheckedRef.current = true;
    getOnboarding()
      .then((s) => { if (s.status === 'IN_PROGRESS') window.location.hash = 'onboarding'; })
      .catch(() => { /* no bloquear la app por esto */ });
  }, [isAuthenticated, user, stores, accountBlocked]);

  // Las dos cambian la sección ANTES de que llegue el `hashchange`: por eso marcan ellas mismas que
  // se navegó dentro de la app.
  // P-075 (D-1): «Vender» no es una página: lleva al punto de venta (`destinationOf`).
  const navigate = (nextSection: AppSection) => {
    navigatedInAppRef.current = true;
    window.location.hash = destinationOf(nextSection);
    setSection(getSectionFromHash());
    setHash(window.location.hash);
  };

  const navigateTo = (destination: string) => {
    navigatedInAppRef.current = true;
    window.location.hash = destination === 'vender' ? destinationOf('vender') : destination;
    setSection(getSectionFromHash());
    setHash(window.location.hash);
  };

  if (isAuthLoading || isInitLoading) return <LoadingScreen />;

  if (initError || !authInit) {
    return (
      <main className="error-screen">
        <span className="error-screen__icon">!</span>
        <h1>No pudimos abrir la aplicación</h1>
        <p>{initError}</p>
        <button className="button button--primary" onClick={() => void loadAuthInit()}>Reintentar</button>
      </main>
    );
  }

  // ONB-004 (FI-70): sin ningún dueño activo ya no hay un formulario anónimo para crear el primero; el
  // primer negocio se crea desde Plataforma.
  if (authInit.needsSetup || authInit.requiresBootstrap) return <SetupPendingPage />;

  if (!isAuthenticated) return <LoginPage />;

  // Fase 25A: si la cuenta esta bloqueada por lifecycle, se muestra la pantalla neutral (con logout)
  // en lugar de la app operativa.
  if (accountBlocked) return <AccountBlockedPage archived={accountBlocked === 'ARCHIVED'} />;

  // Fase 16B-2: espejo de la matriz de permisos backend, solo para experiencia — si alguien fuerza
  // la URL de una sección que su rol no puede usar, se le explica en vez de dejarlo entrar a una
  // pantalla que solo va a fallar contra el backend. La seguridad real sigue viviendo en el backend.
  const sectionGuard = SECTION_GUARDS[section];
  const accessDenied = sectionGuard ? !sectionGuard(user?.role) : false;

  return (
    <AppLayout activeSection={accessDenied ? 'dashboard' : shellSectionForHash(section, hash)} onNavigate={navigate}>
      {accessDenied && <AccessDeniedPage onReturn={() => navigate('dashboard')} />}
      {!accessDenied && section === 'dashboard' && <DashboardHomePage onNavigate={navigateTo} />}
      {!accessDenied && section === 'owner-dashboard' && <OwnerDashboardPage onNavigate={navigateTo} />}
      {!accessDenied && section === 'users' && <UsersPage />}
      {!accessDenied && section === 'stores' && <StoresPage />}
      {!accessDenied && section === 'sales' && <SalesReceiptsPage />}
      {/* P-075: la llave es la dirección a la que se NAVEGÓ (hashchange o menú), no la de la barra: las pestañas de
          Inventario la reescriben con replaceState, y otro render del router no debe remontar la página (perdería los filtros). */}
      {!accessDenied && section === 'inventory' && <InventoryPage area="inventory" initialTab={getInventoryTabFromHash()} key={hash} />}
      {!accessDenied && section === 'purchases' && <InventoryPage area="purchases" initialTab="purchasing" key="purchases" />}
      {!accessDenied && section === 'recipes' && <InventoryPage area="recipes" initialTab="recipes" key="recipes" />}
      {!accessDenied && section === 'waste' && <WastePage />}
      {!accessDenied && section === 'counts' && <CountSheetsPage />}
      {!accessDenied && section === 'shifts' && <ShiftClosePage />}
      {!accessDenied && section === 'expenses' && <ExpensesPage />}
      {!accessDenied && section === 'other-sales' && <OtherSalesPage />}
      {/* Rediseño · Bloque E: el alta de producto con ruta propia, fuera del menú (P-029). */}
      {!accessDenied && section === 'nuevo-producto' && <NuevoProductoPage arrivedInApp={navigatedInAppRef.current} />}
      {!accessDenied && section === 'void-requests' && <VoidRequestsPage />}
      {!accessDenied && section === 'catalog-approvals' && <CatalogApprovalsPage />}
      {!accessDenied && section === 'forms' && <FormsPage />}
      {!accessDenied && section === 'operational-forms' && <OperationalFormsPage />}
      {!accessDenied && section === 'integrations' && <LoyverseIntegrationPage />}
      {/* ONB-007: plan y forma de pago del negocio (el dueño administra; el gerente consulta). */}
      {!accessDenied && section === 'billing' && <BillingPage />}
    </AppLayout>
  );
}

const SECTION_GUARDS: Partial<Record<AppSection, (role: 'OWNER' | 'MANAGER' | 'EMPLOYEE' | undefined) => boolean>> = {
  users: canManageUsers,
  'owner-dashboard': canViewOwnerDashboard,
  // Bloque B: la misma regla con la que «Vender» aparece en el menú (ver `canOpenPos`).
  vender: canOpenPos,
  sales: (role) => role === 'OWNER' || role === 'MANAGER',
  inventory: (role) => role === 'OWNER' || role === 'MANAGER',
  // F1: Mermas para los tres roles (registrar); la bandeja y la anulación las filtra la pantalla y el backend.
  waste: canRecordWaste,
  // F4: Conteos para los tres roles (contar su lista); armar y aprobar lo filtran la pantalla y el backend.
  counts: canCountAssigned,
  // Fase 21A: un EMPLOYEE ahora puede entrar a Compras (solo su tienda) — mismo permiso que ya
  // gatea el botón "Nueva compra" y la navegación.
  purchases: canManagePurchases,
  recipes: (role) => role === 'OWNER' || role === 'MANAGER',
  shifts: canCreateShiftClose,
  expenses: canViewExpenses,
  'other-sales': canViewOtherSales,
  // Bloque E: la misma regla con la que el acceso rápido ofrece «Nuevo producto» (ver `canCreateProduct`).
  'nuevo-producto': canCreateProduct,
  'void-requests': canViewVoidRequests,
  'catalog-approvals': (role) => role === 'OWNER' || role === 'MANAGER',
  integrations: (role) => role === 'OWNER' || role === 'MANAGER',
  // ONB-007: la misma regla con la que «Plan y facturación» aparece en el menú (ver `canViewBilling`).
  billing: canViewBilling,
};

function getSectionFromHash(): AppSection {
  // P-075 (D-1): `#vender` (la página intermedia, que ya no existe) lleva al punto de venta. Se reemplaza la
  // entrada, sin navegar: un enlace guardado abre lo mismo que tocar «Vender».
  const redirect = legacyHashRedirect(window.location.hash);
  if (redirect) window.history.replaceState(window.history.state, '', `#${redirect}`);
  return sectionFromHash(window.location.hash);
}

function getInventoryTabFromHash() {
  const tab = window.location.hash.replace('#', '').split('/')[1];
  return tab === 'movements' || tab === 'transfers' || tab === 'consumption' || tab === 'ingredients' || tab === 'units' || tab === 'presentations'
    ? tab
    : 'stock';
}
